<template>
  <div>
    <SiteNav solid />
    <main id="main" class="trust" tabindex="-1">
      <div class="container">
        <header class="trust__head">
          <p class="eyebrow">Privacy &amp; terms</p>
          <h1>Clear choices.<br /><span>Considered rules.</span></h1>
          <p>
            Know how your conversations work, what information the service uses
            and how to take control of your account.
          </p>
          <span class="trust__status"
            >{{ LEGAL_STATUS }} · October 1, 2026</span
          >
        </header>
        <div class="trust__docs">
          <router-link
            v-for="link in legalLinks"
            :key="link.key"
            :to="link.path"
            class="trust__doc"
          >
            <span class="eyebrow">{{ legalDocs[link.key].category }}</span>
            <h2>
              {{ legalDocs[link.key].title }} <span aria-hidden="true">↗</span>
            </h2>
            <p>{{ link.description }}</p>
            <span class="trust__read">Read document →</span>
          </router-link>
        </div>
        <section class="trust__choices" aria-labelledby="choices-title">
          <div>
            <p class="eyebrow">Before you share</p>
            <h2 id="choices-title">Know your chat mode.</h2>
          </div>
          <div>
            <h3>Secure chats</h3>
            <p>
              End-to-end encrypted messages on supported platforms. Delivery
              metadata and recipient copies still exist.
            </p>
          </div>
          <div>
            <h3>Ordinary &amp; group chats</h3>
            <p>
              Cloud history with server-managed encryption. The service
              processes message content; these chats are not end-to-end
              encrypted.
            </p>
          </div>
        </section>
        <div class="trust__contact">
          <h2>Need help with a privacy request?</h2>
          <a
            :href="`mailto:${SUPPORT_EMAIL}?subject=Allading%20privacy%20request`"
            >{{ SUPPORT_EMAIL }} ↗</a
          >
          <p>
            Share enough to identify your request. Keep passwords, verification
            codes and your backup PIN private.
          </p>
        </div>
      </div>
    </main>
    <SiteFooter />
  </div>
</template>
<script setup lang="ts">
import SiteNav from "@/components/site/SiteNav.vue";
import SiteFooter from "@/components/site/SiteFooter.vue";
import { LEGAL_STATUS, legalDocs, legalLinks } from "@/content/legal";
import { SUPPORT_EMAIL } from "@/content/links";
</script>
<style scoped>
.trust {
  padding: calc(var(--nav-h) + 64px) 0 88px;
  background: linear-gradient(#f1eff8 0, #fff 650px);
}
.trust__head {
  max-width: 800px;
  margin-bottom: 56px;
}
.trust__head .eyebrow {
  color: #6551a5;
}
h1 {
  font-family: var(--font-display);
  font-size: clamp(44px, 6vw, 80px);
  font-weight: 500;
  letter-spacing: -0.055em;
  line-height: 1.08;
  margin: 24px 0;
}
h1 span {
  color: #6551a5;
}
.trust__head > p:last-of-type {
  font-size: 19px;
  color: var(--text-secondary);
  max-width: 630px;
}
.trust__status {
  display: inline-block;
  font-size: 12px;
  margin-top: 24px;
  color: #51417e;
}
.trust__docs {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}
.trust__doc {
  display: flex;
  flex-direction: column;
  padding: 32px;
  background: #fff;
  border: 1px solid #ded9ed;
  border-radius: 16px;
  transition: border-color 180ms ease, box-shadow 180ms ease;
}
.trust__doc:hover {
  border-color: #8673b3;
  box-shadow: 0 12px 32px #2514430b;
}
.trust__doc h2 {
  display: flex;
  gap: 16px;
  justify-content: space-between;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 27px;
  letter-spacing: -0.035em;
  margin: 20px 0 12px;
}
.trust__doc h2 span {
  color: #6551a5;
}
.trust__doc p {
  color: var(--text-secondary);
  font-size: 15px;
  max-width: 400px;
}
.trust__read {
  color: #6551a5;
  font-size: 13px;
  padding-top: 28px;
  margin-top: auto;
}
.trust__choices {
  margin-top: 64px;
  border-radius: 16px;
  background: #17182d;
  color: #fff;
  padding: 40px;
  display: grid;
  grid-template-columns: 1.1fr 1fr 1fr;
  gap: 36px;
}
.trust__choices .eyebrow {
  color: #c1bbff;
}
.trust__choices h2 {
  font-size: 30px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: -0.03em;
  margin-top: 16px;
}
.trust__choices h3 {
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 16px;
}
.trust__choices p:not(.eyebrow) {
  color: #d0cedf;
  font-size: 14px;
}
.trust__contact {
  margin-top: 48px;
}
.trust__contact h2 {
  font-size: 24px;
  font-weight: 500;
  letter-spacing: -0.03em;
}
.trust__contact a {
  display: inline-block;
  padding: 12px 0;
  color: #6551a5;
  text-decoration: underline;
  text-underline-offset: 4px;
  overflow-wrap: anywhere;
}
.trust__contact p {
  color: var(--text-secondary);
  font-size: 13px;
}
@media (max-width: 767px) {
  .trust {
    padding-top: calc(var(--nav-h) + 40px);
  }
  .trust__docs {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  .trust__doc {
    padding: 24px;
  }
  .trust__choices {
    grid-template-columns: 1fr;
    padding: 28px;
    gap: 28px;
    margin-top: 40px;
  }
}
</style>
